import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'

import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import { reportRangeSearchSchema } from '@/features/reports/components/report-range-search'
import { StockReportPage } from '@/features/reports/pages/stock-report'
import { requireCapability } from '@/lib/auth'

import type { StockFilters, StockPaging } from '@/types'

const PAGE_SIZE = 50

/* Reports → Inventory: figures, not charts. The range, the list ("view") and
   its filters live in the URL so a list can be shared. Charts are in
   Analytics → Inventory. */
const searchSchema = reportRangeSearchSchema.extend({
  view: z.enum(['valuation', 'movement', 'adjustments', 'receiving', 'metrc', 'reorder', 'freshness', 'dead', 'check']).optional(),
  category: z.string().optional(),
  cannabis: z.enum(['yes', 'no']).optional(),
  page: z.number().int().min(1).optional(),
  limit: z.number().int().min(1).max(200).optional(),
})

export const Route = createFileRoute('/_dashboard/reports/inventory')({
  beforeLoad: requireCapability('VIEW_REPORTS'),
  validateSearch: searchSchema,
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: InventoryReportRoute,
})

function InventoryReportRoute() {
  const { view, category, cannabis, page, limit, ...selection } = Route.useSearch()
  const navigate = Route.useNavigate()
  const filters: StockFilters = { view, category, cannabis }
  const paging: StockPaging = { page: page ?? 1, limit: limit ?? PAGE_SIZE }
  return (
    <StockReportPage
      selection={selection}
      onSelectionChange={(next) => navigate({ search: (prev) => ({ ...prev, ...next, page: undefined }), replace: true })}
      filters={filters}
      onFiltersChange={(next) =>
        navigate({ search: (prev) => ({ ...prev, ...next, view: next.view === 'valuation' ? undefined : next.view, page: undefined }), replace: true })
      }
      paging={paging}
      onPagingChange={(next) => navigate({ search: (prev) => ({ ...prev, page: next.page > 1 ? next.page : undefined, limit: next.limit !== PAGE_SIZE ? next.limit : undefined }), replace: true })}
    />
  )
}
